import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import Seo, { JsonLd } from "../components/Seo";
import Layout from "../components/Layout";
import { pick, useSite } from "../useSite";

const HERO =
  "https://images.unsplash.com/photo-1544551763-46a013bb70d5?auto=format&fit=crop&w=2400&q=80";

const fallbackHero = {
  title: "The island, planned around you",
  subtitle: "Tailor-made trips · Your traveling partner",
  body: "Private drivers. Quiet hotels. Sri Lanka, from the rock fortress to the reef.",
  image: HERO,
  video: "/hero.mp4?v=galle",
  link: "/enquire",
  linkLabel: "Plan my trip",
  link2: "/journeys",
  linkLabel2: "Explore journeys",
};
const fallbackIntro = {
  title: "Ayubowan Sri Lanka",
  subtitle: "The first welcome",
  image: "/images/ayubowan-sl-beach.jpg",
  body: "Ayubowan means may you live long. It is the greeting SriLankan Airlines offers at the aircraft door, palms together, and the one the island offers when you step onto it.\n\nWe keep that welcome through the days that follow. A private driver, hotels with room to breathe, mornings in the tea hills and evenings on the south coast. Temples, festivals, and the reef, arranged around the way you want to travel.",
};
const fallbackJourneys = {
  title: "Private days, clear prices",
  subtitle: "Journeys",
  body: "These are starting shapes, not fixed coaches. Every route can stretch, soften, or change hotel once we know who is traveling.",
  link: "/journeys",
  linkLabel: "All packages",
};
const fallbackClips = ["/videos/beachside.mp4?v=palm"];
const fallbackJournal = { title: "From the road", subtitle: "Journal" };

export default function Home() {
  const videoRef = useRef(null);
  const { block, ofKind } = useSite();
  const [packages, setPackages] = useState([]);
  const [posts, setPosts] = useState([]);
  const hero = pick(block("hero"), fallbackHero) || {};
  const intro = pick(block("home-intro"), fallbackIntro) || {};
  const journeysHeading = pick(block("home-journeys"), fallbackJourneys) || {};
  const journalHeading = pick(block("home-journal"), fallbackJournal) || {};
  const introParts = String(intro.body || "").split(/\n\n+/).filter(Boolean);
  const poster = hero.image || HERO;
  const videoSrc = hero.video || "/hero.mp4?v=galle";
  const clipItems = ofKind("clip");
  const playlist = clipItems === null
    ? fallbackClips
    : clipItems.map((item) => item.video).filter(Boolean);
  const sources = playlist.length ? playlist : [videoSrc].filter(Boolean);
  const [clipIndex, setClipIndex] = useState(0);
  const currentVideo = sources[clipIndex % Math.max(sources.length, 1)] || "";

  useEffect(() => {
    api("/api/packages").then(setPackages).catch(() => {});
    api("/api/posts").then(setPosts).catch(() => {});
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !currentVideo) return;
    const keepPlaying = () => {
      const nearEnd = Number.isFinite(video.duration) && video.currentTime > video.duration - 0.3;
      if (video.ended || nearEnd) return;
      if (video.paused) video.play().catch(() => {});
    };
    const onEnded = () => setClipIndex((index) => (index + 1) % Math.max(sources.length, 1));
    video.play().catch(() => {});
    video.addEventListener("ended", onEnded);
    video.addEventListener("pause", keepPlaying);
    return () => {
      video.removeEventListener("ended", onEnded);
      video.removeEventListener("pause", keepPlaying);
    };
  }, [currentVideo, sources.length]);

  return (
    <Layout>
      <Seo
        title="Vishmi Travels (Pvt) Limited"
        description="Private Sri Lanka journeys for couples, families, and solo travelers. Cultural Triangle, tea country, wildlife, and the south coast."
        path="/"
        image={poster}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "TravelAgency",
          name: "Vishmi Travels",
          url: "https://www.vishmitravels.com",
          telephone: "+94767845585",
          email: "info@vishmitravels.com",
          address: { "@type": "PostalAddress", addressCountry: "LK" },
        }}
      />
      <section className="hero" style={{ backgroundImage: `url(${poster})` }}>
        {currentVideo ? (
          <video
            key={currentVideo}
            ref={videoRef}
            className="hero-video"
            autoPlay
            muted
            playsInline
            loop={sources.length < 2}
            preload="auto"
            poster={poster}
            aria-hidden="true"
          >
            <source src={currentVideo} type="video/mp4" />
          </video>
        ) : null}
        <div className="hero-inner">
          {hero.subtitle ? <p className="eyebrow">{hero.subtitle}</p> : null}
          {hero.title ? <h1>{hero.title}</h1> : null}
          {hero.body ? <p>{hero.body}</p> : null}
          <div className="hero-actions">
            {hero.linkLabel ? <Link className="btn" to={hero.link || "/enquire"}>{hero.linkLabel}</Link> : null}
            {hero.linkLabel2 ? <Link className="btn ghost" to={hero.link2 || "/journeys"}>{hero.linkLabel2}</Link> : null}
          </div>
        </div>
      </section>

      <section className="section ayubowan-band">
        <div className="wrap ayubowan">
          <div className="ayubowan-copy">
            {intro.subtitle ? <p className="kicker">{intro.subtitle}</p> : null}
            {intro.title ? (
              <h2 className="display ayubowan-title">
                <span className="ayubowan-word">
                  <span className="ayubowan-a">
                    <img className="ayubowan-greet" src="/images/ayubowan-beach-boy.png?v=hat" alt="" />
                    A
                  </span>
                  yubowan
                </span>
                <span className="ayubowan-rest">{String(intro.title).replace(/^ayubowan/i, "").trim()}</span>
              </h2>
            ) : null}
            {introParts[0] ? <p className="lead">{introParts[0]}</p> : null}
            {introParts.slice(1).map((paragraph) => (
              <p className="muted" key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </div>
      </section>

      <section className="section cream journeys-band">
        <div className="wrap">
          <div className="split">
            <div>
              {journeysHeading.subtitle ? <p className="kicker">{journeysHeading.subtitle}</p> : null}
              {journeysHeading.title ? <h2 className="display">{journeysHeading.title}</h2> : null}
            </div>
            {journeysHeading.body ? <p className="muted">{journeysHeading.body}</p> : null}
          </div>
          <div className="card-grid" style={{ marginTop: 32 }}>
            {packages.slice(0, 3).map((item) => (
              <Link
                key={item.slug}
                to={`/journeys/${item.slug}`}
                className="package-card"
                style={{ backgroundImage: `url(${item.heroImage})` }}
              >
                <div>
                  <p className="eyebrow">{item.days} days · {item.currency} {item.price}</p>
                  <h3>{item.title}</h3>
                </div>
              </Link>
            ))}
          </div>
          <p style={{ marginTop: 22 }}>
            {journeysHeading.linkLabel ? (
              <Link className="btn dark" to={journeysHeading.link || "/journeys"}>{journeysHeading.linkLabel}</Link>
            ) : null}
          </p>
        </div>
      </section>

      <section className="section journal-band">
        <div className="wrap">
          {journalHeading.subtitle ? <p className="kicker">{journalHeading.subtitle}</p> : null}
          {journalHeading.title ? <h2 className="display">{journalHeading.title}</h2> : null}
          <div className="story-grid" style={{ marginTop: 28 }}>
            {posts.slice(0, 3).map((post) => (
              <Link
                key={post.slug}
                to={`/journal/${post.slug}`}
                className="story-card"
                style={{ backgroundImage: `url(${post.coverImage})` }}
              >
                <div>
                  <p className="eyebrow">{post.category}</p>
                  <h3>{post.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
